import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { initializePaddle } from '@paddle/paddle-js';
import crownImg from './crown.png';
import {
  CLIENT_ROOT,
  PADDLE_CLIENT_TOKEN,
  PADDLE_ENV,
  PADDLE_PRICE_ID_MONTHLY,
  PADDLE_PRICE_ID_PRO_MONTHLY,
  PADDLE_PRICE_ID_ULTRA_MONTHLY,
} from 'env';
import { changePlan, createPortalSession } from '../api/paddleApi';
import {
  isHobbyPlan,
  isPaidPlan,
  isProPlan,
  isUltraPlan,
  normalizePlan,
  planIncludes,
} from '../utils/plans';

const plans = {
  free: [
    'Up to 3 boards',
    'Maximum 50 pins per board',
    '336,000 monthly AI allowance (about 13 Better chats)',
  ],
  hobby: [
    'Unlimited boards',
    'Up to 150 pins per board',
    '2,738,400 monthly AI allowance (about 110 Better chats)',
    'Private boards',
    'Board management tools',
    'Your private information is protected — questions and data are not used to train AI models',
  ],
  pro: [
    'Unlimited boards',
    'Unlimited pins per board',
    '5,544,000 monthly AI allowance (about 220 Better chats)',
    'Private boards',
    'Board management tools',
    'Co-edit canvases with other Pro users',
    'Your private information is protected — questions and data are not used to train AI models',
  ],
  ultra: [
    'Everything in Pro',
    '16,741,200 monthly AI allowance (about 660 Better chats)',
    'Unlimited boards and pins',
    'Co-edit canvases',
    'Best for heavy daily AI use',
    'Your private information is protected — questions and data are not used to train AI models',
  ],
};

const HOBBY_MONTHLY = 4.9;
const PRO_MONTHLY = 9.9;
const ULTRA_MONTHLY = 29.9;

function formatUsd(amount) {
  return Number(amount).toFixed(2);
}

function FeatureList({ items }) {
  return (
    <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map(item => (
        <Stack
          key={item}
          component="li"
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
        >
          <CheckRoundedIcon
            sx={{ fontSize: 20, color: '#16a34a', mt: '2px', flexShrink: 0 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {item}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function PlanCard({ children, highlighted = false, badge }) {
  return (
    <Card
      elevation={highlighted ? 4 : 0}
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: highlighted ? 'transparent' : 'divider',
        background: highlighted
          ? 'linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #fbbf24, #f59e0b, #d97706) border-box'
          : '#fff',
        borderWidth: highlighted ? 2 : 1,
        boxShadow: highlighted
          ? '0 20px 40px -12px rgba(245, 158, 11, 0.25)'
          : '0 1px 3px rgba(0, 0, 0, 0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: highlighted ? 'translateY(-4px)' : 'translateY(-2px)',
          boxShadow: highlighted
            ? '0 24px 48px -12px rgba(245, 158, 11, 0.32)'
            : '0 8px 24px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {badge ? (
        <Chip
          label={badge}
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            fontWeight: 600,
            fontSize: '0.7rem',
            letterSpacing: '0.04em',
            bgcolor: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fde68a',
          }}
        />
      ) : null}
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 3, sm: 3.5 },
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
}

function ManageButton({ loading, onClick }) {
  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={onClick}
      disabled={loading}
      sx={{
        mt: 3,
        py: 1.25,
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        borderColor: '#f59e0b',
        color: '#b45309',
        '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' },
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : 'Manage subscription'}
    </Button>
  );
}

function UpgradeButton({ loading, disabled, label, onClick }) {
  return (
    <Button
      variant="contained"
      fullWidth
      onClick={onClick}
      disabled={disabled || loading}
      sx={{
        mt: 3,
        py: 1.25,
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        bgcolor: '#111',
        boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        '&:hover': { bgcolor: '#222', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' },
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : label}
    </Button>
  );
}

const SubscriptionPricing = () => {
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingAction, setLoadingAction] = useState(null);
  const [error, setError] = useState('');

  const queryState = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isSuccess = queryState.get('success') === '1';
  const isCanceled = queryState.get('canceled') === '1';

  React.useEffect(() => {
    if (!isSuccess) return undefined;
    // Checkout redirects here before Paddle's subscription webhook has usually reached
    // the server, so refetch a few times instead of once.
    const refetch = () => dispatch({ type: 'SET_USER', method: 'get', path: '/auth/user' });
    const timers = [0, 2000, 5000, 10000].map(ms => setTimeout(refetch, ms));
    return () => timers.forEach(clearTimeout);
  }, [dispatch, isSuccess]);

  const plan = normalizePlan(user?.subscriptionStatus);
  const isHobby = isHobbyPlan(plan);
  const isPro = isProPlan(plan);
  const isUltra = isUltraPlan(plan);
  const isPaid = isPaidPlan(plan);
  const hasSubscription = Boolean(user?.subscriptionId);

  const openCheckout = async (tier) => {
    if (!user) {
      dispatch({ type: 'OPEN_SIGN_$' });
      return;
    }

    try {
      setLoadingAction(tier);
      setError('');

      const paddle = await initializePaddle({
        environment: PADDLE_ENV,
        token: PADDLE_CLIENT_TOKEN,
      });

      if (!paddle) {
        setError('Paddle SDK failed to initialize.');
        return;
      }

      const priceId =
        tier === 'ultra'
          ? PADDLE_PRICE_ID_ULTRA_MONTHLY
          : tier === 'pro'
            ? PADDLE_PRICE_ID_PRO_MONTHLY
            : PADDLE_PRICE_ID_MONTHLY;

      if (!priceId) {
        setError('Paddle price ID is not configured.');
        return;
      }

      const customer = user.paddleCustomerId
        ? { id: user.paddleCustomerId }
        : user.email
          ? { email: user.email }
          : undefined;

      paddle.Checkout.open({
        settings: {
          displayMode: 'overlay',
          successUrl: `${CLIENT_ROOT}/pricing?success=1`,
          ...(customer ? { allowLogout: false } : {}),
        },
        items: [{ priceId, quantity: 1 }],
        ...(customer ? { customer } : {}),
        customData: {
          userId: String(user._id),
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to open Paddle checkout.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleManage = async () => {
    try {
      setLoadingAction('manage');
      setError('');
      const json = await createPortalSession();
      if (json?.url) {
        window.location.href = json.url;
      } else {
        setError('Portal URL was not returned.');
      }
    } catch (err) {
      setError(err.message || 'Failed to open subscription management.');
    } finally {
      setLoadingAction(null);
    }
  };

  /** Upgrade/downgrade: update existing sub when possible; otherwise open checkout. */
  const handleSelectPlan = async (tier) => {
    if (!user) {
      dispatch({ type: 'OPEN_SIGN_$' });
      return;
    }

    const target = normalizePlan(tier === 'ultra' ? 'ULTRA' : tier === 'pro' ? 'PRO' : 'HOBBY');
    if (plan === target) return;

    if (hasSubscription && isPaid) {
      try {
        setLoadingAction(tier);
        setError('');
        await changePlan(target);
        // The server applied the plan synchronously; refetch now, then route through the
        // router (not history.replaceState) so the success banner renders.
        dispatch({ type: 'SET_USER', method: 'get', path: '/auth/user' });
        navigate('/pricing?success=1', { replace: true });
      } catch (err) {
        if (err.code === 'NO_SUBSCRIPTION') {
          await openCheckout(tier);
          return;
        }
        setError(err.message || 'Failed to change plan.');
      } finally {
        setLoadingAction(null);
      }
      return;
    }

    await openCheckout(tier);
  };

  const hobbyCta = () => {
    if (isHobby) {
      return <ManageButton loading={loadingAction === 'manage'} onClick={handleManage} />;
    }
    if (planIncludes(plan, 'HOBBY')) {
      return (
        <UpgradeButton
          loading={false}
          disabled
          label="Included in your plan"
          onClick={() => {}}
        />
      );
    }
    return (
      <UpgradeButton
        loading={loadingAction === 'hobby'}
        label="Upgrade to Hobby"
        onClick={() => handleSelectPlan('hobby')}
      />
    );
  };

  const proCta = () => {
    if (isPro) {
      return <ManageButton loading={loadingAction === 'manage'} onClick={handleManage} />;
    }
    if (planIncludes(plan, 'PRO')) {
      return (
        <UpgradeButton
          loading={false}
          disabled
          label="Included in Ultra"
          onClick={() => {}}
        />
      );
    }
    return (
      <UpgradeButton
        loading={loadingAction === 'pro'}
        label={isPaid ? 'Upgrade to Pro' : 'Upgrade to Pro'}
        onClick={() => handleSelectPlan('pro')}
      />
    );
  };

  const ultraCta = () => {
    if (isUltra) {
      return <ManageButton loading={loadingAction === 'manage'} onClick={handleManage} />;
    }
    return (
      <UpgradeButton
        loading={loadingAction === 'ultra'}
        label={isPaid ? 'Upgrade to Ultra' : 'Get Ultra'}
        onClick={() => handleSelectPlan('ultra')}
      />
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f7f5',
        backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(251, 191, 36, 0.12), transparent)',
        pt: { xs: 10, sm: 12 },
        pb: { xs: 6, sm: 10 },
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem' },
              letterSpacing: '-0.02em',
              color: 'text.primary',
            }}
          >
            Simple, transparent pricing
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 560, lineHeight: 1.6 }}
          >
            Start free, grow with Hobby or Pro, or go Ultra for the largest AI allowance.
          </Typography>
        </Stack>

        {isSuccess ? (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            Subscription updated successfully.
          </Alert>
        ) : null}
        {isCanceled ? (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Checkout was canceled.
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1fr 1fr 1fr 1fr',
            },
            gap: { xs: 2, sm: 2.5 },
            alignItems: 'stretch',
          }}
        >
          <PlanCard>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
              Free
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1, mb: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                $0
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Forever free for personal use
            </Typography>

            <Box sx={{ flex: 1 }}>
              <FeatureList items={plans.free} />
            </Box>

            <Button
              variant="outlined"
              fullWidth
              disabled={!isPaid && !!user}
              onClick={() => {
                if (!user) dispatch({ type: 'OPEN_SIGN_$' });
              }}
              sx={{
                mt: 3,
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'divider',
                color: 'text.primary',
              }}
            >
              {!user ? 'Get started free' : !isPaid ? 'Current plan' : 'Free tier'}
            </Button>
          </PlanCard>

          <PlanCard highlighted={isHobby} badge={isHobby ? 'Active' : undefined}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
              Hobby
            </Typography>

            <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1, mb: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                ${HOBBY_MONTHLY.toFixed(2)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                /mo
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 20 }}>
              Billed monthly, cancel anytime
            </Typography>

            <Box sx={{ flex: 1 }}>
              <FeatureList items={plans.hobby} />
            </Box>

            {hobbyCta()}
          </PlanCard>

          <PlanCard highlighted={isPro} badge={isPro ? 'Active' : 'Popular'}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                Pro
              </Typography>
              <Box
                component="img"
                src={crownImg}
                alt=""
                sx={{ width: 20, height: 20, objectFit: 'contain' }}
              />
            </Stack>

            <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1, mb: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                ${formatUsd(PRO_MONTHLY)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                /mo
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 20 }}>
              Billed monthly, cancel anytime
            </Typography>

            <Box sx={{ flex: 1 }}>
              <FeatureList items={plans.pro} />
            </Box>

            {proCta()}
          </PlanCard>

          <PlanCard highlighted={isUltra} badge={isUltra ? 'Active' : 'Largest'}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
              Ultra
            </Typography>

            <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1, mb: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                ${formatUsd(ULTRA_MONTHLY)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                /mo
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 20 }}>
              Pro features, with about three times the AI allowance
            </Typography>

            <Box sx={{ flex: 1 }}>
              <FeatureList items={plans.ultra} />
            </Box>

            {ultraCta()}
          </PlanCard>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          sx={{ mt: 4, lineHeight: 1.6 }}
        >
          Secure checkout powered by Paddle. The allowance is shared by all AI on the board. Better is the normal chat; Fast uses less, and Pro uses about eight times more. Upgrade prorates immediately; cancel anytime from Manage subscription.
        </Typography>
      </Container>
    </Box>
  );
};

export default SubscriptionPricing;
